/**
 * 全部批量任务列表 Hook
 * 用于获取和管理所有批量任务的历史记录
 * 支持分页、备注搜索和自动轮询未完成任务
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import type { BatchTaskListItem, BatchTaskListParams } from '@/types/task'
import { BATCH_TASK_STATUS } from '@/types/task'
import { listAllBatchTasks } from '@/services/taskService'

/**
 * 轮询配置
 */
const POLLING_CONFIG = {
  /** 轮询间隔（毫秒）- 5秒 */
  INTERVAL: 5000,
} as const

/**
 * 已完成的状态列表（不需要轮询）
 */
const FINISHED_STATUSES = [
  BATCH_TASK_STATUS.COMPLETED,
  BATCH_TASK_STATUS.PARTIAL_FAILED,
  BATCH_TASK_STATUS.FAILED,
  BATCH_TASK_STATUS.PAUSED,
  BATCH_TASK_STATUS.CANCELLED,
] as const

/**
 * Hook 返回类型
 */
export interface UseAllBatchTasksReturn {
  /** 批量任务列表 */
  items: BatchTaskListItem[]
  /** 是否正在加载 */
  isLoading: boolean
  /** 错误信息 */
  error: Error | null
  /** 记录总数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  pageSize: number
  /** 总页数 */
  totalPages: number
  /** 切换页码 */
  setPage: (page: number) => void
  /** 设置备注搜索关键词 */
  setRemarkSearch: (remark: string) => void
  /** 当前备注搜索关键词 */
  remarkSearch: string
  /** 设置工作流 ID 筛选 */
  setWorkflowId: (workflowId: string) => void
  /** 当前工作流 ID 筛选 */
  workflowId: string
  /** 刷新数据 */
  refresh: () => void
}

/**
 * 全部批量任务列表 Hook
 * @param initialPageSize 初始每页数量（默认 10）
 * @returns 批量任务列表数据和状态
 */
export function useAllBatchTasks(initialPageSize = 10): UseAllBatchTasksReturn {
  const [items, setItems] = useState<BatchTaskListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(initialPageSize)
  const [remarkSearch, setRemarkSearchState] = useState('')
  const [workflowId, setWorkflowIdState] = useState('')

  // 轮询定时器
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)

  /**
   * 计算总页数
   */
  const totalPages = useMemo((): number => {
    return Math.max(1, Math.ceil(total / pageSize))
  }, [total, pageSize])

  /**
   * 检查列表中是否有未完成的任务
   */
  const hasIncompleteTask = useMemo((): boolean => {
    return items.some(
      (item) => !FINISHED_STATUSES.includes(item.status as typeof FINISHED_STATUSES[number])
    )
  }, [items])

  /**
   * 停止轮询
   */
  const stopPolling = useCallback((): void => {
    if (pollingTimerRef.current !== null) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }
  }, [])

  /**
   * 获取批量任务列表
   * @param showLoading 是否显示加载状态
   */
  const fetchTasks = useCallback(
    async (showLoading = true): Promise<void> => {
      if (showLoading) {
        setIsLoading(true)
      }
      setError(null)

      try {
        const params: BatchTaskListParams = {
          page,
          page_size: pageSize,
        }

        if (remarkSearch !== '') {
          params.remark = remarkSearch
        }

        if (workflowId !== '') {
          params.workflow_id = workflowId
        }

        const response = await listAllBatchTasks(params)

        if (!isMountedRef.current) return

        setItems(response.items)
        setTotal(response.total)
      } catch (err) {
        if (!isMountedRef.current) return
        const errorMessage =
          err instanceof Error ? err.message : '获取任务列表失败'
        setError(new Error(errorMessage))
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false)
        }
      }
    },
    [page, pageSize, remarkSearch, workflowId]
  )

  /**
   * 安排下一次轮询
   */
  const schedulePolling = useCallback((): void => {
    stopPolling()

    pollingTimerRef.current = setTimeout(() => {
      void fetchTasks(false)
    }, POLLING_CONFIG.INTERVAL)
  }, [fetchTasks, stopPolling])

  /**
   * 刷新数据
   */
  const refresh = useCallback((): void => {
    stopPolling()
    void fetchTasks(true)
  }, [fetchTasks, stopPolling])

  /**
   * 设置备注搜索关键词（同时重置页码）
   */
  const setRemarkSearch = useCallback((remark: string): void => {
    setRemarkSearchState(remark)
    setPage(1)
  }, [])

  /**
   * 设置工作流 ID 筛选（同时重置页码）
   */
  const setWorkflowId = useCallback((id: string): void => {
    setWorkflowIdState(id)
    setPage(1)
  }, [])

  // 初始加载和参数变化时重新加载
  useEffect(() => {
    isMountedRef.current = true
    void fetchTasks(true)

    return (): void => {
      isMountedRef.current = false
      stopPolling()
    }
  }, [fetchTasks, stopPolling])

  // 根据任务状态启动或停止轮询
  useEffect(() => {
    if (hasIncompleteTask && isMountedRef.current) {
      schedulePolling()
    } else {
      stopPolling()
    }

    return stopPolling
  }, [hasIncompleteTask, schedulePolling, stopPolling])

  return {
    items,
    isLoading,
    error,
    total,
    page,
    pageSize,
    totalPages,
    setPage,
    setRemarkSearch,
    remarkSearch,
    setWorkflowId,
    workflowId,
    refresh,
  }
}
